import { Store } from 'Application/Store/Store'
import { Scene, Vector3 } from 'three'
import { addSceneElements } from 'Domain/Scene/Builder/SceneBuilder'
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine'
import { initCameras } from 'Domain/Scene/Handler/CameraHandler'
import { LightingSetup, setLighting } from 'Domain/Scene/Provider/LightProvider'
import { OnMeshClick, refreshModifications } from 'Domain/Mesh/Handler/MeshSelectorEvent'
import { Control } from 'Domain/Panel/Model/OrbitControl'
import { showComparativeElement } from 'Domain/Scene/Handler/AnimationHandler'
import { setLightSetupLayer } from 'Domain/Scene/Handler/LayerHandler'
import { buildRender } from 'Domain/Scene/Handler/RenderHandler'
import { registerContextLossHandler } from 'Domain/Scene/Handler/WebGLContextHandler'
import { GetReferencesModifications } from 'Domain/Scene/Builder/GetReferencesModifications'
import { ShotDone, ShotScene, disposeSnapshotRenderer } from 'Domain/Scene/Manager/SnapShotManager'
import { buildComposer } from 'Domain/Mesh/Handler/Composer'
import { createGrids } from '../../Mesh/Handler/Grid'
import { ATTACHMENT_VISIBLE, STRIPPING_VISIBLE, SET_MODIFICATIONS } from 'Application/Store/Actions/ViewportTypes'
import { cloneMalocAndSetupForComparativeView } from '../Handler/ComparativeViewHandler'
import { nextRenderTick, setPhase, INIT_PHASES } from 'Domain/Scene/Manager/InitializationOrchestrator'
import { loadAndDisplayAnnouncements } from 'Application/Hooks/useViewerAnnouncements'
import { toogleAnimationVisible } from './ReduxActionCreators'
import { meshSelectionStore } from 'Domain/Mesh/State/MeshSelectionStore'

const scene = new Scene()
threeEngine.setScene(scene)
threeEngine.setCenter(new Vector3())

export const getScene = () => scene

const initializeRenderer = (canvas) => {
  initCameras()
  const renderer = buildRender('sceneViewer', canvas)
  buildComposer(renderer, scene)
  threeEngine.setRenderer(renderer)
  registerContextLossHandler(renderer)
  return renderer
}

const initializeLighting = (renderer) => {
  const lightSetup = LightingSetup(threeEngine.getCenter())
  setLightSetupLayer(lightSetup)
  setLighting(scene, renderer)
  scene.add(lightSetup)
}

const initializeCamera = (renderer) => {
  const mainCamera = threeEngine.getCamera('main')
  threeEngine.setControlManager(Control)
  threeEngine.setupControls(mainCamera, renderer.domElement)
  mainCamera.lookAt(threeEngine.getCenter())
}

const createSnapshotRenderer = (canvas) => {
  const snapshotSize = { offsetHeight: 400, offsetWidth: 400 }
  return buildRender('sceneSnapshot', canvas, true, snapshotSize)
}

const activateTreatmentVisibility = () => {
  const animationState = Store.getState().AnimationReducer
  if (!animationState.loaded) return

  const treatmentMetas = animationState.metas?.treatment
  if (!treatmentMetas) return

  const hasAttachments = treatmentMetas['attachment'] !== 'none'
  const hasIpr = treatmentMetas['ipr'] !== 'none'

  if (hasAttachments) Store.dispatch({ type: ATTACHMENT_VISIBLE, payload: true })
  if (hasIpr) Store.dispatch({ type: STRIPPING_VISIBLE, payload: true })
}

const setupComparativeView = async () => {
  createGrids()
  await nextRenderTick()
  cloneMalocAndSetupForComparativeView(scene)
  await nextRenderTick()
}

const scheduleSnapshot = (snapshotRenderer, projectMetas) => {
  const hasNoImage = projectMetas.format.image === undefined
  if (!hasNoImage) {
    disposeSnapshotRenderer(snapshotRenderer)
    return
  }

  const SNAPSHOT_DELAY = 5000
  setTimeout(() => {
    const canTakeSnapshot = !ShotDone() && Store.getState().AnimationReducer.loaded
    if (canTakeSnapshot) {
      ShotScene(snapshotRenderer, threeEngine.getCenter(), scene, projectMetas)
    } else {
      disposeSnapshotRenderer(snapshotRenderer)
    }
  }, SNAPSHOT_DELAY)
}

export const initializeScene = async (canvas, model, projectMetas) => {
  setPhase(INIT_PHASES.SCENE_CREATED)

  const renderer = initializeRenderer(canvas)
  initializeLighting(renderer)

  const { maxiExtraData, mandiExtraData } = await addSceneElements(model, threeEngine.getCenter(), scene, renderer)

  setPhase(INIT_PHASES.MODELS_LOADED)

  const modifications = await GetReferencesModifications()
  meshSelectionStore.setModifications(modifications)
  Store.dispatch({ type: SET_MODIFICATIONS, payload: modifications })
  refreshModifications()
  Store.dispatch(toogleAnimationVisible('flex'))

  const snapshotRenderer = createSnapshotRenderer(canvas)

  initializeCamera(renderer)
  showComparativeElement(false)

  setPhase(INIT_PHASES.ANIMATION_READY)

  OnMeshClick(canvas, renderer, threeEngine.getControls(), maxiExtraData, mandiExtraData)

  setPhase(INIT_PHASES.COMPARATIVE_READY)

  await setupComparativeView()
  activateTreatmentVisibility()
  scheduleSnapshot(snapshotRenderer, projectMetas)

  loadAndDisplayAnnouncements(Store.dispatch)

  return { renderer, maxiExtraData, mandiExtraData }
}
